import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { useParams, useSearchParams } from "react-router-dom";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import GlobalStudyMismatchWarning from "Components/Common/GlobalStudyMismatchWarning";
import { BreadcrumbContext } from "Layouts/Header";
import { useGlobalStudy } from "context/StudyContext";
import { LINE_CLEARANCE_PATHS } from "Routes/paths";
import LineClearanceStepper from "../components/LineClearanceStepper";
import AddEditLineClearancePharmacist from "../components/AddEditLineClearancePharmacist";
import AddEditLineClearanceQA from "../components/AddEditLineClearanceQA";
import { Step1DataPackage } from "../helpers/lineClearance.types";
import { useLineClearanceService } from "../api/useLineClearance";

const AddLineClearance: React.FC = () => {
  /* -----------------------------------------------------------------
   * 1. Router & State Setup
   * ----------------------------------------------------------------- */
  const { id: paramId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine Record ID (Route Param in Edit mode, Search Param in Add mode)
  const editId = paramId || searchParams.get("id");
  const stepFromUrl = Number(searchParams.get("step")) || 1;

  // Set Document Title
  document.title = `${editId ? "Edit" : "Add"} Line Clearance | ZENTIXS ORBIT`;

  // Context & Global State
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { selectedStudy: globalStudy } = useGlobalStudy();

  // Stepper State
  const [currentStep, setCurrentStep] = useState<number>(stepFromUrl);
  const [isStep1Saved, setIsStep1Saved] = useState<boolean>(Boolean(editId));
  const [createdLineClearanceId, setCreatedLineClearanceId] = useState<number | null>(
    editId ? Number(editId) : null
  );

  /* -----------------------------------------------------------------
   * 2. API Queries & Derived Data
   * ----------------------------------------------------------------- */
  // Fetch Study Dropdown Options
  const { DropdownQuery: studySelectAllQuery } = useLineClearanceService({
    getDropdown: true,
  });

  // Map Study Options for Warning Banner Check
  const studyOptions = useMemo(
    () =>
      studySelectAllQuery.data?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studycode,
        studyid: study.studyid,
      })) || [],
    [studySelectAllQuery.data]
  );

  // Check if active Global Study exists in options list
  const isStudyInOptions = useMemo(() => {
    if (!globalStudy?.studyid || studyOptions.length === 0) return true;
    return studyOptions.some(
      (s: any) => String(s.studyid) === String(globalStudy.studyid)
    );
  }, [globalStudy?.studyid, studyOptions]);

  /* -----------------------------------------------------------------
   * 3.(useEffect)
   * ----------------------------------------------------------------- */
  // Sync Edit ID with Created Line Clearance ID
  useEffect(() => {
    if (editId) {
      setCreatedLineClearanceId(Number(editId));
      setIsStep1Saved(true);
    }
  }, [editId]);

  // Fallback Guard: If step 2 accessed without valid ID, return to Step 1
  useEffect(() => {
    if (currentStep === 2 && !createdLineClearanceId && !editId) {
      handleStepChange(1);
    }
  }, [currentStep, createdLineClearanceId, editId]);

  // Sync Header Breadcrumbs
  useEffect(() => {
    const step1Label = editId ? "Edit Line Clearance" : "Add Line Clearance";
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Line Clearance", link: LINE_CLEARANCE_PATHS.LIST },
      { text: currentStep === 1 ? step1Label : "Line Clearance Verification (QA)" },
    ]);
    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [currentStep, editId]);

  /* -----------------------------------------------------------------
   * 4. Event & Navigation Handlers
   * ----------------------------------------------------------------- */
  // Update Current Step in State and URL Search Parameters
  const handleStepChange = (step: number) => {
    setCurrentStep(step);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", String(step));
        return next;
      },
      { replace: true }
    );
  };

  // Step 1 Form Save Success Callback
  const handleStep1SaveSuccess = (dataPackage: Step1DataPackage) => {
    const createdId = dataPackage.createdData?.lineclearanceid;

    if (createdId) {
      setCreatedLineClearanceId(Number(createdId));
      // Only append ?id= in URL during Add mode (where route param doesn't exist)
      if (!paramId) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("id", String(createdId));
            return next;
          },
          { replace: true }
        );
      }
    }
    setIsStep1Saved(true);
  };

  // Navigation Handlers
  const handleNextStep = () => {
    if (isStep1Saved) {
      handleStepChange(2);
    }
  };

  const handleBackToStep1 = () => {
    handleStepChange(1);
  };

  /* -----------------------------------------------------------------
   * 5. Main Component Render
   * ----------------------------------------------------------------- */
  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        {/* Global Study Mismatch Warning */}
        <GlobalStudyMismatchWarning
          globalStudy={globalStudy}
          studyOptions={studyOptions}
          isLoaded={!studySelectAllQuery.isLoading}
          isPresent={isStudyInOptions}
        />

        {/*Stepper Navigation Header */}
        <LineClearanceStepper
          currentStep={currentStep}
          onStepChange={handleStepChange}
          isStep1Saved={isStep1Saved}
        />

        {/* Step Views */}
        {currentStep === 1 ? (
          <AddEditLineClearancePharmacist
            editId={editId}
            isStep1Saved={isStep1Saved}
            onSaveSuccess={handleStep1SaveSuccess}
            onNext={handleNextStep}
          />
        ) : (
          <AddEditLineClearanceQA
            lineclearanceid={createdLineClearanceId}
            onBack={handleBackToStep1}
          />
        )}
      </Container>
    </div>
  );
};

export default AddLineClearance;
